import { useId, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SENDING_MODES,
  WORDING_CATALOG,
  asciiName,
  can,
  reminderStepLabel,
  reviewLinkProblem,
  type EDITABLE_EMAIL_KINDS,
  type EmailStatus,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { listOf } from '../../lib/format';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Toggle } from '../ui/Toggle';
import { Button } from '../ui/Button';
import { field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
  worstError,
  worstState,
} from '../ui/QueryStates';
import { useLanding } from '../ui/useLanding';
import { useOnline } from '../ui/useConnection';
import { IconMail } from '../icons';
import { AskedNotToHear } from './AskedNotToHear';
import { CustomerRepliesField } from './CustomerRepliesField';
import { EditWordingSlot } from './EditWordingSlot';
import { ResendConfirmation } from './ResendConfirmation';
import { SendTestEmail } from './SendTestEmail';
import { DomainSetup } from './DomainSetup';
import {
  EMAIL_ANCHOR,
  SENDING_MODE_LABEL,
  fallbackLine,
  noticeView,
  routeState,
  todayLine,
  waitingRows,
} from './emailView';

/** While the card is open it asks again this often. */
export const EMAIL_STATUS_REFRESH_MS = 60_000;
/** An answer older than this is stale: the all-clear is not said over it. */
export const EMAIL_STATUS_FRESH_MS = 90_000;

/**
 * Settings → Email (`#email`, after Automations; MAP 249 spec §9, §13, ticket 07). The owner
 * edits; the office reads it all, with "Only the owner can change this."
 *
 * One read answers the card and the app-wide banner (`email.status`): the route, today's count,
 * what waits and why, whether the shop is paused or held, and the notices. The sections that
 * own their own reads (the reply address, the automatic emails' switches, the suppression list)
 * draw whatever that read says, so a failed status read never takes the ticket composer's
 * `#customer-replies` landing with it.
 *
 * ⚠️ NEVER "ALL GOOD" ON AN ANSWER THAT DID NOT COME BACK. No status yet (loading, failed,
 * offline with nothing cached): the contract's `UnresolvedState`, nothing else. A cached status
 * whose refresh failed says so (`StaleNotice`). And the one all-clear sentence ("Nothing is
 * holding your customer emails") shows only for an answer read SINCE THIS CARD MOUNTED, not
 * stale, not paused, and with a connection: an old cached "nothing waiting" is not a fact about
 * now, and neither is a good answer once the connection has gone.
 *
 * Mount points for later tickets: 08's domain setup and 10's mailbox connect (marked where they
 * go). 05's "Edit wording" is on every automatic-email row; 06's three switches and the review
 * link's editor are in their sections.
 */
export function EmailCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const { role } = useSession();
  const owner = can(role, 'org', 'manage');
  /**
   * - `refetchOnMount: 'always'`: the banner above every page reads the same answer first, so
   *   within `staleTime` this card would never read it ITSELF, and `fresh` below (read since this
   *   card mounted) would hold the all-clear back for no reason. The card asks on every arrival.
   * - `staleTime` / `refetchInterval`: while the card is open it asks again every
   *   `EMAIL_STATUS_REFRESH_MS`, and an answer older than `EMAIL_STATUS_FRESH_MS` (the interval
   *   plus a margin, so a refresh in flight does not blink the line) is stale: no all-clear.
   */
  const q = useQuery({
    ...trpc.email.status.queryOptions(),
    refetchOnMount: 'always',
    staleTime: EMAIL_STATUS_FRESH_MS,
    refetchInterval: EMAIL_STATUS_REFRESH_MS,
  });
  const online = useOnline();
  const state = shownState(q, q.data);
  /**
   * Only an answer that is TRUE NOW may say all is well (Codex 07-r1 #1): read since this card
   * mounted, not stale, not paused, and with a connection. Going offline after a good read
   * leaves `isSuccess` and `isFetchedAfterMount` true and `fetchStatus` idle, so the
   * connection is asked separately; the known notices and counts stay on screen either way.
   */
  const fresh =
    online &&
    q.isSuccess &&
    q.isFetchedAfterMount &&
    !q.isStale &&
    q.fetchStatus !== 'paused';

  return (
    <SectionCard id={id} title="Email">
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {!owner ? (
        <p className="mb-2 text-xs text-muted-foreground">
          Only the owner can change this.
        </p>
      ) : null}
      <div className="@container divide-y divide-border [&>*]:py-3 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
        {!q.data ? (
          <div>
            <UnresolvedState
              state={unresolved(state)}
              what="your email settings"
              icon={IconMail}
              error={q.error}
              onRetry={() => void q.refetch()}
              skeleton="h-24"
            />
          </div>
        ) : (
          <>
            <StatusSection s={q.data} fresh={fresh} />
            <SendingFromSection s={q.data} owner={owner} />
            <TodaySection s={q.data} />
          </>
        )}
        <div>
          <CustomerRepliesField owner={owner} />
        </div>
        <AutomaticEmailsSection owner={owner} />
        {q.data ? <ReviewLinkSection s={q.data} owner={owner} /> : null}
        <div>
          <AskedNotToHear />
        </div>
      </div>
    </SectionCard>
  );
}

/** A part of the card a link can land on: scrolled to and marked (`useLanding`). */
function Section({
  anchor,
  title,
  children,
}: {
  anchor: string;
  title?: string;
  children: ReactNode;
}) {
  const landing = useLanding<HTMLDivElement>(anchor);
  return (
    <div>
      <div
        ref={landing.ref}
        id={anchor}
        data-landed={landing.lit ? 'true' : undefined}
        className={`scroll-mt-[var(--sw-anchor-offset)] p-1 transition-colors ${landing.mark}`}
      >
        {title ? <h3 className="mb-1 text-sm font-medium">{title}</h3> : null}
        {children}
      </div>
    </div>
  );
}

/**
 * What needs saying about the shop's email, most urgent first: the banner's notices, each with
 * its way out. Where every shop-wide notice's link lands (`#email-status`).
 */
function StatusSection({ s, fresh }: { s: EmailStatus; fresh: boolean }) {
  return (
    <Section anchor={EMAIL_ANCHOR.status}>
      {!s.on ? (
        <Notice tone="warn">
          Email isn&apos;t switched on yet, so nothing is sent to customers: each email is
          recorded as not sent. Quotes and invoices still have a link you can send
          yourself.
        </Notice>
      ) : s.notices.length > 0 ? (
        <ul className="space-y-2">
          {s.notices.map((n) => {
            const v = noticeView(n, s);
            return (
              <li key={v.kind} data-email-notice={v.kind}>
                <Notice tone={v.tone}>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{v.text}</span>
                    {v.resend ? <ResendConfirmation /> : null}
                    {v.link.hash !== EMAIL_ANCHOR.status ? (
                      <Link
                        to="/settings"
                        hash={v.link.hash}
                        className="font-medium underline underline-offset-2"
                      >
                        {v.link.label}
                      </Link>
                    ) : null}
                  </span>
                </Notice>
              </li>
            );
          })}
        </ul>
      ) : fresh ? (
        <p className="text-sm text-muted-foreground">
          Nothing is holding your customer emails.
        </p>
      ) : null}
    </Section>
  );
}

/**
 * "Sending from": the three ways to send, each with where it stands. The owner chooses one the
 * server says can send (`ready`); ServWell's address is always there. A chosen route that stops
 * working falls back to ServWell's address on its own, and the line under the list says so.
 */
function SendingFromSection({ s, owner }: { s: EmailStatus; owner: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const org = useQuery(trpc.org.info.queryOptions());
  const [error, setError] = useState<string | null>(null);
  const update = useMutation(
    trpc.email.update.mutationOptions({
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );
  const fallback = fallbackLine(s);
  return (
    <Section anchor={EMAIL_ANCHOR.sendingFrom}>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">Sending from</legend>
        <div className="space-y-1">
          {SENDING_MODES.map((mode) => {
            const ready = s.ready[mode];
            return (
              <label
                key={mode}
                className={`flex min-h-11 items-start gap-2 py-1 text-sm ${
                  owner && ready ? 'cursor-pointer' : ''
                }`}
              >
                <input
                  type="radio"
                  name="email-sending-mode"
                  className="mt-0.5 size-4 shrink-0 aria-disabled:opacity-50"
                  value={mode}
                  checked={s.sendingMode === mode}
                  // Never `disabled` while saving (it drops focus to <body>): ignored instead.
                  disabled={!owner || !ready}
                  aria-disabled={update.isPending || undefined}
                  onChange={() => {
                    if (update.isPending || mode === s.sendingMode) return;
                    update.mutate({ sendingMode: mode });
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{SENDING_MODE_LABEL[mode]}</span>
                    {s.route === mode ? (
                      <StatusBadge tone="success">In use</StatusBadge>
                    ) : null}
                  </span>
                  <span className="block break-words text-xs text-muted-foreground">
                    {routeState(mode, s)}
                    {mode === 'servwell' && org.data
                      ? `. Customers see “${asciiName(org.data.name)} via ServWell”.`
                      : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {/* Ticket 08: "Your domain" (setup, records, Check again, a test, Disconnect; the owner
          writes, the office reads). TICKET 10 mounts "Your mailbox"'s Connect / Reconnect /
          Disconnect beside it. */}
      <DomainSetup owner={owner} on={s.on} shopName={org.data?.name ?? null} />
      {fallback ? (
        <p className="mt-1 text-xs font-medium text-[var(--sw-danger-text)]">
          {fallback}
        </p>
      ) : null}
      {error ? (
        <div className="mt-2">
          <Notice tone="danger" alert>
            {error}
          </Notice>
        </div>
      ) : null}
      {owner && s.on ? <SendTestEmail /> : null}
    </Section>
  );
}

/** "Today: X of Y sent · Z waiting", and what waits and why (`#email-today`). */
function TodaySection({ s }: { s: EmailStatus }) {
  const rows = waitingRows(s);
  return (
    <Section anchor={EMAIL_ANCHOR.today}>
      <p className="text-sm font-medium tabular-nums">{todayLine(s)}</p>
      <p className="text-xs text-muted-foreground">
        {s.today.firstDay
          ? `Your shop’s first day: at most ${s.today.cap} emails today. `
          : ''}
        The count starts again at midnight, your shop’s time.
      </p>
      {rows.length > 0 ? (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.wait} data-waiting={r.wait} className="flex gap-2">
              <span className="w-6 shrink-0 text-right font-medium tabular-nums">
                {r.count}
              </span>
              <span className="min-w-0">{r.line}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

type EditableKind = (typeof EDITABLE_EMAIL_KINDS)[number];

/**
 * "Automatic emails": every kind of email ServWell sends a shop's customers, with its on/off
 * where a switch exists today, its timing, and "Edit wording" (ticket 05's sheet,
 * `EditWordingSlot`). The quote follow-up and payment reminder switches are the SAME settings the
 * Automations and Getting paid cards show (one setting, shown in two places). Quote approved,
 * review request and service reminder (ticket 06) are switched here only; under the last two, the
 * SERVER's sentence says what still stands between them and their first email (no mailing
 * address, no review link, no service type that reminds), and turning one on while a required one
 * stands is refused in those words.
 */
function AutomaticEmailsSection({ owner }: { owner: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const automations = useQuery(trpc.automations.get.queryOptions());
  const policy = useQuery(trpc.collections.policy.queryOptions());
  const [error, setError] = useState<string | null>(null);
  const done = {
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (e: { message: string }) => {
      setError(e.message);
      void qc.invalidateQueries();
    },
  };
  const saveAutomations = useMutation(trpc.automations.update.mutationOptions(done));
  const savePolicy = useMutation(trpc.collections.updatePolicy.mutationOptions(done));
  const busy = saveAutomations.isPending || savePolicy.isPending;
  const state = worstState(
    shownState(automations, automations.data),
    shownState(policy, policy.data),
  );

  const rows: {
    kind: EditableKind;
    when: string;
    /** The server's sentence: what still stands between this kind and its first email. */
    needs?: string | null;
    control: ReactNode;
  }[] = [];
  const clicked = (
    <span className="text-xs text-muted-foreground">When you press Send</span>
  );
  /** One of ticket 06's switches: the owner's, on `automations.update`. */
  const automatic = (
    key: 'quoteApprovedEnabled' | 'reviewRequestEnabled' | 'serviceReminderEnabled',
    ariaLabel: string,
    on: boolean,
  ) => (
    <Toggle
      label={on ? 'On' : 'Off'}
      ariaLabel={ariaLabel}
      on={on}
      disabled={!owner || busy}
      focusableWhenDisabled
      onChange={(next) => saveAutomations.mutate({ [key]: next, ...idem() })}
    />
  );
  if (automations.data && policy.data) {
    const settings = automations.data.settings;
    const blockers = automations.data.blockers;
    const followups = automations.data.settings.quoteFollowupOffsetDays;
    const reminders = policy.data.policy.reminderOffsetDays;
    rows.push(
      {
        kind: 'invoice',
        when: 'Goes when you send an invoice.',
        control: clicked,
      },
      {
        kind: 'quote',
        when: 'Goes when you send a quote.',
        control: clicked,
      },
      {
        kind: 'payment_reminder',
        when: `Goes ${listOf(reminders.map(reminderStepLabel))}, while an invoice is unpaid.`,
        control: (
          <Toggle
            label={policy.data.policy.remindersEnabled ? 'On' : 'Off'}
            ariaLabel="Payment reminders"
            on={policy.data.policy.remindersEnabled}
            disabled={!owner || busy}
            focusableWhenDisabled
            onChange={(on) => savePolicy.mutate({ remindersEnabled: on, ...idem() })}
          />
        ),
      },
      {
        kind: 'quote_follow_up',
        when: `Goes ${listOf(
          followups.map((d) => `${d} day${d === 1 ? '' : 's'}`),
        )} after a quote is sent, while nobody has answered.`,
        control: (
          <Toggle
            label={automations.data.settings.quoteFollowupEnabled ? 'On' : 'Off'}
            ariaLabel="Quote follow-ups"
            on={automations.data.settings.quoteFollowupEnabled}
            disabled={!owner || busy}
            focusableWhenDisabled
            onChange={(on) =>
              saveAutomations.mutate({ quoteFollowupEnabled: on, ...idem() })
            }
          />
        ),
      },
      {
        kind: 'quote_approved',
        when: 'Confirms a quote the customer approved, or that you recorded their yes. Goes at once.',
        control: automatic(
          'quoteApprovedEnabled',
          'Quote approved emails',
          settings.quoteApprovedEnabled,
        ),
      },
      {
        kind: 'review_request',
        when: 'Asks for a review at 10 AM the day after a job is closed; at most one per customer in 90 days.',
        needs: blockers.review_request,
        control: automatic(
          'reviewRequestEnabled',
          'Review requests',
          settings.reviewRequestEnabled,
        ),
      },
      {
        kind: 'service_reminder',
        when: 'At 9 AM, once the months set on a service type have passed since a customer’s last job of that type.',
        needs: blockers.service_reminder,
        control: automatic(
          'serviceReminderEnabled',
          'Service reminders',
          settings.serviceReminderEnabled,
        ),
      },
    );
  }

  return (
    <Section anchor={EMAIL_ANCHOR.automaticEmails} title="Automatic emails">
      <StaleNotice
        show={
          refreshFailed(automations, automations.data) ||
          refreshFailed(policy, policy.data)
        }
        onRetry={() => {
          void automations.refetch();
          void policy.refetch();
        }}
      />
      {!automations.data || !policy.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your automatic emails"
          icon={IconMail}
          error={worstError(automations.error, policy.error)}
          onRetry={() => {
            void automations.refetch();
            void policy.refetch();
          }}
          skeleton="h-16"
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            The automatic ones go between 8 AM and 7 PM, your shop&apos;s time. Follow-ups
            and reminders are the same switches as under Automations.
          </p>
          {error ? (
            <div className="mt-2">
              <Notice tone="danger" alert>
                {error}
              </Notice>
            </div>
          ) : null}
          <ul className="mt-1 divide-y divide-border">
            {rows.map((r) => (
              <li
                key={r.kind}
                data-email-kind={r.kind}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
              >
                <span className="min-w-0 flex-1 basis-56">
                  {/* One name per kind: ticket 05's catalog (the wording sheet says the same). */}
                  <span className="block text-sm font-medium">
                    {WORDING_CATALOG[r.kind].label}
                  </span>
                  <span className="block text-xs text-muted-foreground">{r.when}</span>
                  {r.needs ? (
                    <span className="block text-xs font-medium text-[var(--sw-warn-text)]">
                      {r.needs}
                    </span>
                  ) : null}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {r.control}
                  <EditWordingSlot kind={r.kind} />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

/**
 * "Review link" (`#review-link`, ticket 06): where a review request sends the customer. The owner
 * edits it: the screen says a link of the wrong SHAPE as it is typed (`reviewLinkProblem`, the
 * server's own first check: one of the shapes the review sites publish, exactly); the server
 * also reads what a customer would see in its page name (a phone number, a brand scammers pose as)
 * and refuses in words, shown under the field. Saving it empty takes it off. A review request
 * already written keeps the link it was written with. The office reads it.
 */
function ReviewLinkSection({ s, owner }: { s: EmailStatus; owner: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation(
    trpc.email.update.mutationOptions({
      onSuccess: () => {
        setDraft(null);
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );
  const value = draft ?? s.reviewLink ?? '';
  const changed = value.trim() !== (s.reviewLink ?? '');
  const shape = value.trim() ? reviewLinkProblem(value) : null;
  const said = shape ?? error;
  return (
    <Section anchor={EMAIL_ANCHOR.reviewLink} title="Review link">
      {owner ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (save.isPending || !changed || shape) return;
            save.mutate({ reviewLink: value.trim() || null });
          }}
        >
          <input
            id={`${id}-link`}
            className={`${field} min-w-0 flex-1 basis-64`}
            type="url"
            inputMode="url"
            aria-label="Review link"
            aria-invalid={said ? true : undefined}
            aria-describedby={said ? `${id}-problem` : `${id}-help`}
            placeholder="https://g.page/r/…/review"
            value={value}
            readOnly={save.isPending}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
          />
          <Button type="submit" aria-disabled={save.isPending || !changed || !!shape}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {said ? (
            <p
              id={`${id}-problem`}
              role="alert"
              className="basis-full text-xs text-[var(--sw-danger-text)]"
            >
              {said}
            </p>
          ) : null}
        </form>
      ) : s.reviewLink ? (
        <a
          href={s.reviewLink}
          target="_blank"
          rel="noreferrer noopener"
          className="break-all text-sm font-medium text-[var(--sw-brand-text)] underline underline-offset-2"
        >
          {s.reviewLink}
        </a>
      ) : (
        <p className="text-sm">Not set.</p>
      )}
      <p id={`${id}-help`} className="text-xs text-muted-foreground">
        Where a review request sends your customers: your Google review link (g.page/r/…
        or search.google.com/local/writereview?placeid=…), or your Yelp, Facebook,
        Nextdoor or BBB review page.
      </p>
    </Section>
  );
}
